import React, { useState, useRef, useEffect, useImperativeHandle, useCallback } from 'react';
import { testNotification, getNotificationState } from '../utils/notifications';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const uid = () => Date.now() + Math.random();
const DEFAULT_MSG = [{ id: uid(), role: 'assistant', content: 'Systems online. All diagnostics nominal.\nHow can I assist you?' }];

function Chat({ ref, ...props }) {
  // Stable ref to avoid stale closures in callbacks
  const onDataChangeRef = useRef(props.onDataChange);
  onDataChangeRef.current = props.onDataChange;
  const [messages, setMessages] = useState(() => { try { const s = localStorage.getItem('chat_msgs'); if (s) { const parsed = JSON.parse(s); return parsed.map(m => ({ ...m, id: m.id || uid() })); } return DEFAULT_MSG; } catch { return DEFAULT_MSG; } });
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const inputRef = useRef(null);
  const mrRef = useRef(null);
  const chunksRef = useRef([]);
  const abortRef = useRef(null);

  // Scroll to bottom — use container.scrollTop instead of scrollIntoView (more reliable)
  const messagesRef = useRef(null);
  const scrollToBottom = (instant) => {
    const el = messagesRef.current;
    if (!el) return;
    if (instant) { el.scrollTop = el.scrollHeight; }
    else { el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' }); }
  };
  // Scroll on new messages
  useEffect(() => { requestAnimationFrame(() => scrollToBottom(false)); }, [messages]);
  // Scroll when loading starts (Analyzing... appears)
  useEffect(() => { if (loading) requestAnimationFrame(() => scrollToBottom(true)); }, [loading]);
  // Scroll when panel opens (wait for CSS transition to finish)
  useEffect(() => {
    if (props.active) { const t = setTimeout(() => scrollToBottom(true), 380); return () => clearTimeout(t); }
  }, [props.active]);
  useEffect(() => { const save = () => { try { localStorage.setItem('chat_msgs', JSON.stringify(messages.slice(-50).map(m => ({ role: m.role, content: m.content })))); } catch {} }; if ('requestIdleCallback' in window) { const id = requestIdleCallback(save, { timeout: 2000 }); return () => cancelIdleCallback(id); } else { save(); } }, [messages]);

  // Auto-resize textarea when input changes (covers STT, paste, typing)
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 100) + 'px';
    }
  }, [input]);

  // Cleanup: abort in-flight fetch and stop MediaRecorder tracks on unmount
  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
      if (mrRef.current && mrRef.current.stream) {
        mrRef.current.stream.getTracks().forEach(t => t.stop());
      }
    };
  }, []);

  const safeJson = async r => { const t = await r.text(); try { return JSON.parse(t); } catch { return { error: `Error (${r.status})` }; } };
  const clearChat = async () => { setMessages(DEFAULT_MSG); localStorage.removeItem('chat_msgs'); try { await fetch(`${API_URL}/clear`, { method: 'POST' }); } catch {} };
  useImperativeHandle(ref, () => ({ clear: clearChat }));

  const send = async text => {
    if (!text.trim() || loading) return;

    // Client-side commands
    if (text.trim().toLowerCase() === '/test-notif') {
      setMessages(p => [...p, { id: uid(), role: 'user', content: text }]); setInput('');
      const state = getNotificationState();
      const result = await testNotification();
      setMessages(p => [...p, { id: uid(), role: 'assistant', content: `Notification test:\n• Supported: ${state.supported}\n• Permission: ${state.permission}\n• Service Worker: ${state.swReady ? 'ready' : 'not ready'}\n• Native Bridge: ${state.nativeBridge ? 'yes (APK)' : 'no (browser)'}\n• Scheduled: ${state.scheduled} task(s)\n\n${result.success ? '✓ ' + result.reason : '✗ ' + result.reason}` }]);
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    const c = new AbortController(); abortRef.current = c;
    setMessages(p => [...p, { id: uid(), role: 'user', content: text }]); setInput(''); setLoading(true);
    let shouldRefresh = false;
    try {
      const r = await fetch(`${API_URL}/chat`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: text }), signal: c.signal });
      const d = await safeJson(r);
      if (!c.signal.aborted) {
        setMessages(p => [...p, { id: uid(), role: 'assistant', content: d.response || d.error || 'Done.', showConfirmButtons: d.pendingConfirmation && d.action === 'PENDING_CONFIRMATION', batchItems: d.batchItems || null }]);
        // Only refresh dashboard when data actually changed (not for chat/clarify responses)
        const dataActions = ['ADD_TASK','ADD_MEMORY','DELETE_TASK','DELETE_MEMORY','UPDATE_TASK','UPDATE_MEMORY','COMPLETE_TASK','BULK_DELETE','BULK_UPDATE','BATCH_SAVE'];
        if (d.action && dataActions.includes(d.action)) shouldRefresh = true;
      }
    } catch (e) { if (e.name !== 'AbortError' && !c.signal.aborted) setMessages(p => [...p, { id: uid(), role: 'assistant', content: 'Signal lost. Retry.' }]); }
    if (!c.signal.aborted) setLoading(false);
    if (shouldRefresh && onDataChangeRef.current) onDataChangeRef.current();
  };

  const confirm = async () => {
    setLoading(true); setMessages(p => p.map(m => m.showConfirmButtons ? { ...m, showConfirmButtons: false } : m));
    try { const r = await fetch(`${API_URL}/confirm`, { method: 'POST' }); const d = await safeJson(r); setMessages(p => [...p, { id: uid(), role: 'assistant', content: d.response || d.error || 'Done.' }]); }
    catch (e) { setMessages(p => [...p, { id: uid(), role: 'assistant', content: `Error: ${e.message}` }]); }
    finally { setLoading(false); }
    if (onDataChangeRef.current) onDataChangeRef.current();
  };
  const cancel = async () => {
    setMessages(p => p.map(m => m.showConfirmButtons ? { ...m, showConfirmButtons: false } : m));
    try { await fetch(`${API_URL}/cancel`, { method: 'POST' }); } catch {}
    setMessages(p => [...p, { id: uid(), role: 'assistant', content: 'Cancelled.' }]);
  };

  // STT — direct, no Llama clean step
  const startRec = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(s, { mimeType: 'audio/webm' }); mrRef.current = mr; chunksRef.current = [];
      mr.ondataavailable = e => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        s.getTracks().forEach(t => t.stop());
        const b = new Blob(chunksRef.current, { type: 'audio/webm' });
        const fd = new FormData(); fd.append('audio', b, 'a.webm');
        try {
          const r = await fetch(`${API_URL}/stt`, { method: 'POST', body: fd });
          const d = await safeJson(r);
          if (d.text) setInput(p => p ? p + ' ' + d.text : d.text);
        } catch (e) { setMessages(p => [...p, { id: uid(), role: 'assistant', content: `Voice error: ${e.message}` }]); }
      };
      mr.start(); setRecording(true);
    } catch (err) { setMessages(p => [...p, { id: uid(), role: 'assistant', content: `Mic error: ${err.name || err.message || 'Access denied'}. Please allow microphone permission in your device settings.` }]); }
  };
  const stopRec = () => { if (mrRef.current && recording) { mrRef.current.stop(); setRecording(false); } };

  return (
    <div className="fri-chat">
      {/* Messages */}
      <div className="fri-messages" ref={messagesRef} aria-live="polite">
        {messages.map((msg, i) => (
          <div key={msg.id || i} className={`fri-msg fri-msg-${msg.role}`}>
            {msg.role === 'assistant' && (
              <div className="fri-msg-avatar"><div className="fri-msg-avatar-core" /></div>
            )}
            <div className="fri-msg-body">
              {msg.role === 'assistant' && <div className="fri-msg-label">FRIDAY</div>}
              {msg.role === 'user' && <div className="fri-msg-label fri-msg-label-user">YOU</div>}
              <div className={`fri-bubble fri-bubble-${msg.role}`}>{msg.content}</div>

              {msg.showConfirmButtons && !loading && (
                <div className="fri-confirm-area">
                  {msg.batchItems ? (
                    <>
                      {msg.batchItems.map((item, idx) => (
                        <div key={idx} className={`fri-confirm-item ${msg._rejectedItems?.includes(idx) ? 'fri-confirm-skipped' : ''}`}>
                          <span className="fri-confirm-item-text">{item.summary.split('\n')[1]?.replace('•','').trim() || `Item ${idx+1}`}</span>
                          {!msg._rejectedItems?.includes(idx) ? (
                            <button className="fri-btn-skip" onClick={() => setMessages(p => p.map((m,mi) => { if(mi!==i) return m; const e=m._rejectedItems||[]; if(e.includes(idx)) return m; return {...m,_rejectedItems:[...e,idx]}; }))}>SKIP</button>
                          ) : <span className="fri-confirm-skipped-label">SKIPPED</span>}
                        </div>
                      ))}
                      <div className="fri-confirm-actions">
                        <button className="fri-btn-confirm" onClick={async()=>{let r,batchLen;setMessages(p=>{const updated=p.map((m,mi)=>{if(mi!==i)return m;r=m._rejectedItems||[];batchLen=m.batchItems.length;return{...m,showConfirmButtons:false};});return updated;});const idx=Array.from({length:batchLen},(_,j)=>j).filter(j=>!r.includes(j));if(idx.length===batchLen)await confirm();else if(!idx.length)await cancel();else{setLoading(true);try{const res=await fetch(`${API_URL}/batch-save`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({indices:idx})});const d=await safeJson(res);setMessages(p=>[...p,{id:uid(),role:'assistant',content:d.response||d.error||'Done.'}])}catch(e){setMessages(p=>[...p,{id:uid(),role:'assistant',content:`Error: ${e.message}`}])}setLoading(false);if(onDataChangeRef.current) onDataChangeRef.current()}}}>
                          EXECUTE ({msg.batchItems.length-(msg._rejectedItems?.length||0)})
                        </button>
                        <button className="fri-btn-cancel" onClick={async()=>{setMessages(p=>p.map((m,mi)=>mi===i?{...m,showConfirmButtons:false}:m));await cancel()}}>ABORT ALL</button>
                      </div>
                    </>
                  ) : (
                    <div className="fri-confirm-actions">
                      <button className="fri-btn-confirm" onClick={async()=>{setMessages(p=>p.map((m,mi)=>mi===i?{...m,showConfirmButtons:false}:m));await confirm()}}>EXECUTE</button>
                      <button className="fri-btn-cancel" onClick={async()=>{setMessages(p=>p.map((m,mi)=>mi===i?{...m,showConfirmButtons:false}:m));await cancel()}}>ABORT</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="fri-msg fri-msg-assistant">
            <div className="fri-msg-avatar"><div className="fri-msg-avatar-core fri-msg-avatar-thinking" /></div>
            <div className="fri-msg-body">
              <div className="fri-msg-label">FRIDAY</div>
              <div className="fri-bubble fri-bubble-assistant fri-thinking">
                <div className="fri-thinking-dots"><span /><span /><span /></div>
                <span className="fri-thinking-text">Analyzing...</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Recording indicator */}
      {recording && (
        <div className="fri-recording-bar">
          <span className="fri-recording-dot" />
          <span>Recording...</span>
        </div>
      )}

      {/* Input */}
      <div className="fri-input-area">
        <div className="fri-input-row">
          <button className={`fri-mic ${recording ? 'fri-mic-active' : ''}`}
            onMouseDown={startRec} onMouseUp={stopRec} onMouseLeave={stopRec}
            onTouchStart={e=>{e.preventDefault();startRec()}} onTouchEnd={e=>{e.preventDefault();stopRec()}} onTouchCancel={stopRec}
            onContextMenu={e=>e.preventDefault()}
            aria-label="Hold to record voice">
            <div className="fri-mic-icon">{recording ? '●' : '◉'}</div>
          </button>
          <textarea ref={inputRef} className="fri-input" value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); } }}
            placeholder="Enter command..." rows={1}
          />
          <button className={`fri-send ${input.trim() ? 'fri-send-ready' : ''}`} onClick={() => send(input)} disabled={loading || !input.trim()} aria-label="Send message">
            <span className="fri-send-icon">▶</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default React.memo(Chat);
